import { Api } from "src/utils/api";
import { ADDS_SALES_API_BASE_URL } from "./ads-sales.constant";

export interface WidgetData {
  sales?: number;
  spend?: number;
  impressions?: number;
  ctr?: number;
  cvr?: number;
  cpc?: number;
  acos?: number;
  roas?: number;
  orders?: number;
  unitSolds?: number;
  clicks?: number;
}

interface WidgetDataResponse {
  ad_sales?: number;
  ad_spend?: number;
  ad_impressions?: number;
  ad_ctr?: number;
  ad_cvr?: number;
  cpc?: number;
  acos?: number;
  roas?: number;
  ad_orders?: number;
  ad_units_sold?: number;
  ad_clicks?: number;
}

export class AdsSalesApi extends Api {
  constructor() {
    super(ADDS_SALES_API_BASE_URL);
  }

  public getAllMetrics = async (
    date_start: string,
    date_end: string,
    saturation: number,
    asin?: string,
  ): Promise<WidgetData | WidgetData[]> => {
    const params: Record<string, any> = { date_start, date_end, saturation };
    if (asin) params.asin = asin;
    return this.get("ads-sales/all", params);
  };

  public getImpressions = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/impressions", { date_start, date_end });
  };

  public getClicks = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/clicks", { date_start, date_end });
  };

  public getOrders = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/orders", { date_start, date_end });
  };

  public getUnitsSold = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/units-sold", { date_start, date_end });
  };

  public getSales = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/sales", { date_start, date_end });
  };

  public getSpend = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/spend", { date_start, date_end });
  };

  public getAcos = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/acos", { date_start, date_end });
  };

  public getRoas = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/roas", { date_start, date_end });
  };

  public getCpc = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/cpc", { date_start, date_end });
  };

  public getCvr = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/cvr", { date_start, date_end });
  };

  public getCtr = async (
    date_start: string,
    date_end: string,
  ): Promise<number> => {
    return await this.get("ads-sales/ctr", { date_start, date_end });
  };

  public getAllMetricsDiff = async (
    date_start: string,
    date_end: string,
    compare_date_start: string,
    compare_date_end: string,
    saturation: number = 0,
  ): Promise<WidgetDataResponse> => {
    return await this.get("ads-sales/all/diff", {
      date_start,
      date_end,
      saturation,
      compare_date_start,
      compare_date_end,
    });
  };

  public getWidgetsComparePromise = async (
    date_start: string,
    date_end: string,
    compare_date_start: string,
    compare_date_end: string,
  ): Promise<Partial<WidgetData>> => {
    try {
      const diffData = await this.getAllMetricsDiff(
        date_start,
        date_end,
        compare_date_start,
        compare_date_end,
        0,
      );

      const {
        ad_sales,
        ad_spend,
        ad_clicks,
        ad_impressions,
        ad_units_sold,
        ad_orders,
        acos,
        roas,
        cpc,
        ad_ctr,
        ad_cvr,
      } = diffData;

      return {
        sales: ad_sales ?? 0,
        spend: ad_spend ?? 0,
        impressions: ad_impressions ?? 0,
        ctr: ad_ctr ?? 0,
        cvr: ad_cvr ?? 0,
        cpc: cpc ?? 0,
        acos: acos ?? 0,
        roas: roas ?? 0,
        orders: ad_orders ?? 0,
        unitSolds: ad_units_sold ?? 0,
        clicks: ad_clicks ?? 0,
      };
    } catch {
      return {};
    }
  };

  public getWidgetDataPromise = async (
    date_start: string,
    date_end: string,
    asin?: string | null,
  ): Promise<Partial<WidgetData>> => {
    const [
      sales,
      spend,
      impressions,
      ctr,
      cvr,
      cpc,
      acos,
      roas,
      orders,
      unitsSold,
      clicks,
    ] = await Promise.all([
      this.getSales(date_start, date_end),
      this.getSpend(date_start, date_end),
      this.getImpressions(date_start, date_end),
      this.getCtr(date_start, date_end),
      this.getCvr(date_start, date_end),
      this.getCpc(date_start, date_end),
      this.getAcos(date_start, date_end),
      this.getRoas(date_start, date_end),
      this.getOrders(date_start, date_end),
      this.getUnitsSold(date_start, date_end),
      this.getClicks(date_start, date_end),
    ]);

    return {
      sales,
      spend,
      impressions,
      ctr,
      cvr,
      cpc,
      acos,
      roas,
      orders,
      unitSolds: unitsSold,
      clicks,
    };
  };
}
